/**
 * Tuning knobs for the CPU. Heuristic scores rate one action at a time; the turn search plays out whole
 * turns and rates the resulting position with `POSITION`. Changes here also change how long win % takes.
 */

/** One-action heuristic weights. */
export const HEURISTIC = {
  /** Score per Prestige gained. */
  prestige: 26,
  /** Score per Prestige stolen. */
  steal: 32,
  /** Score for an action that wins the game. */
  win: 1000,
  /** Extra weight per Prestige when the CPU is still at 0. */
  hunger: 16,
  /** Weight for Prestige a move unlocks on other board badges. Only part of it is cashed this turn. */
  potential: 16,
} as const;

export const SEARCH = {
  /** Most candidate first actions the turn search plays out. */
  width: 5,
  /** Hidden-deck shuffles each candidate is played out against. */
  samples: 3,
  /** Heuristic-score gap below the best option past which a candidate is not played out. Lower is faster. */
  margin: 60,
  /** Weight on the one-action heuristic score, which is on a smaller scale than `POSITION`. */
  prior: 0.5,
  /** Head start for the quick-rated pick when the choice search compares its options. */
  choicePrior: 20,
} as const;

export type CpuDifficulty = 'easy' | 'hard' | 'impossible';

export const CPU_DIFFICULTIES: readonly CpuDifficulty[] = ['easy', 'hard', 'impossible'];

export const isCpuDifficulty = (value: string): value is CpuDifficulty =>
  CPU_DIFFICULTIES.some((level) => level === value);

export const CPU_DIFFICULTY_LABELS: Record<CpuDifficulty, string> = {
  easy: 'Easy',
  hard: 'Hard',
  impossible: 'Impossible',
};

/** Display name for a CPU. Easy is the default, so it is just "CPU". */
export const cpuName = (difficulty: CpuDifficulty): string =>
  difficulty === 'easy' ? 'CPU' : `${CPU_DIFFICULTY_LABELS[difficulty]} CPU`;

type SearchScale = { width: number; samples: number; margin: number };

export type SearchBudget = SearchScale & {
  /** Opponent turns played out after the CPU's own turn before rating the position. */
  horizon: number;
  /** Also plays out each option of a choice (pick a pile, a card, a stat...) instead of the quick rating. */
  searchChoices: boolean;
  /**
   * During the turn search, later actions on this seat are compared too, not assumed greedy.
   * Hard searches the first action and greedies the rest; Impossible re-picks the rest of its turn.
   */
  searchRest: boolean;
  /** Chance per decision of taking the second-best option instead of the best. */
  mistakeRate: number;
  /** A slip only happens when the second-best option rates within this much of the best. */
  mistakeMargin: number;
};

/** Scales the base `SEARCH`. Width and samples are counts, so they round to whole numbers (at least 1). */
const scaleSearch = (scale: SearchScale, rest: Omit<SearchBudget, keyof SearchScale>): SearchBudget => ({
  width: Math.max(1, Math.round(SEARCH.width * scale.width)),
  samples: Math.max(1, Math.round(SEARCH.samples * scale.samples)),
  margin: SEARCH.margin * scale.margin,
  ...rest,
});

/** Turn-search budget per difficulty, as multipliers on the base `SEARCH`. */
export const DIFFICULTY_SEARCH: Record<CpuDifficulty, SearchBudget> = {
  // Greedy, and half the time a close second-best, so it fights but can be outplayed.
  easy: scaleSearch(
    { width: 0.2, samples: 0.67, margin: 1 },
    { horizon: 0, searchChoices: false, searchRest: false, mistakeRate: 0.5, mistakeMargin: 45 }
  ),
  // Plays out every reasonable first action, choices included, to the end of its own turn.
  hard: scaleSearch(
    { width: 1.2, samples: 1, margin: 20 },
    { horizon: 0, searchChoices: true, searchRest: false, mistakeRate: 0, mistakeMargin: 0 }
  ),
  // Wider search, more hidden-card guesses, two opponent turns, and it re-picks the rest of its own turn.
  impossible: scaleSearch(
    { width: 2, samples: 2.67, margin: 20 },
    { horizon: 2, searchChoices: true, searchRest: true, mistakeRate: 0, mistakeMargin: 0 }
  ),
};

/**
 * How far ahead the match feels, for the CPU's face. One point is about one Prestige, so the bands in
 * `CPU_FACE_BY_LEAD` keep their meaning when the rest of the board is even.
 * Each weight is how many of those points one unit of that lead is worth.
 * Who has more actions this turn (or next) is the main scale: a hole makes a Prestige deficit scarier
 * and a lead less safe. A last go still adds a little, so a race to the winning Prestige weighs more.
 */
export const FACE = {
  /** Per Prestige ahead of the opponent, while the match is still open. */
  prestige: 1,
  /** Extra weight from how close the leader is to the winning total. */
  prestigeCloserToWin: 0.35,
  /**
   * Added for each action a last go will not play, on a 28-action scale.
   * A last go with 3 actions left weighs Prestige by `1 + 0.05 * 25`.
   */
  prestigeWeightIncreasePerRemainingAction: 0.05,
  /**
   * How much a longer or shorter turn changes how scary the rest of the board feels.
   * Each action of difference adds this to 1. Down 2 actions, a losing board is multiplied by
   * `1 + 2 * 0.35` (scarier); a winning board is divided by that (less safe). Extra actions flip it.
   */
  actionScalePerAction: 0.35,
  /** Per card in hand ahead. */
  hand: 0.3,
  /** Per card left in deck ahead. Still to draw, so a card here counts for less than one in hand. */
  deck: 0.1,
  /** Per revealed stat point ahead, summed across every axis. */
  stats: 0.2,
  /** Per stat axis this seat leads and can activate a board effect with. A tie, or a lead with nothing to activate, counts for neither seat. */
  statLead: 0.5,
  /** Per action ahead on this turn or the next: extra actions, skipped actions, and a longer or shorter turn. */
  tempo: 1.5,
} as const;

/**
 * Times the scared face-score band (`CPU_FACE_BY_LEAD` scared `scoreAtMost`) a seat must be down
 * before they concede. Easy folds sooner; Impossible holds out longer. The face score itself is
 * the same at every difficulty; only this cutoff changes.
 */
export const FACE_CONCEDE_MULTIPLE: Record<CpuDifficulty, number> = {
  easy: 1.75,
  hard: 2,
  impossible: 2.25,
};

/** How the turn search rates the position once the CPU's turn ends. */
export const POSITION = {
  /** Per Prestige ahead of the opponent. */
  prestigeLead: 100,
  /** Per Prestige board badges could cash next turn, up to the actions that turn still has. */
  potential: 40,
  /** Per board badge whose stat the CPU leads in, so it can activate. */
  liveBadge: 12,
  /** Per point of board badge value (stats plus rarity). */
  boardValue: 3,
  /** Per card in hand, up to `handCap`. */
  handCard: 10,
  handCap: 4,
  /** Per action the CPU will skip next turn. */
  skippedAction: 200,
  /** A finished game, won or lost. */
  gameOver: 100_000,
} as const;
